/**
 * Push-notification helper hook.
 * Handles permission request, service-worker subscription, and syncing the
 * subscription with our backend (/api/notifications/*).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export default function usePushNotifications() {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'unsupported'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [status, setStatus] = useState(null); // {configured, can_broadcast}

  // Load VAPID public key + current status
  useEffect(() => {
    if (!supported) return;
    let mounted = true;
    (async () => {
      try {
        const [keyRes, statusRes] = await Promise.all([
          axios.get(`${API}/api/notifications/vapid-public-key`).catch(() => null),
          axios.get(`${API}/api/notifications/status`, { withCredentials: true }).catch(() => null),
        ]);
        if (!mounted) return;
        if (keyRes?.data?.publicKey) setPublicKey(keyRes.data.publicKey);
        if (statusRes?.data) setStatus(statusRes.data);
      } catch (_) { /* ignore */ }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (mounted) setSubscribed(!!sub);
      } catch (_) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error('Push notifications are not supported on this device/browser.');
    setLoading(true);
    try {
      // Step 1 — Permission
      let perm = Notification.permission;
      if (perm === 'default') {
        try { perm = await Notification.requestPermission(); }
        catch (e) { throw new Error('Browser blocked the permission prompt. Try again from a user click.'); }
      }
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Notifications are blocked. Click the lock icon in the address bar → Site settings → Allow Notifications, then retry.');

      // Step 2 — VAPID key
      let key = publicKey;
      if (!key) {
        try {
          const res = await axios.get(`${API}/api/notifications/vapid-public-key`);
          key = res.data.publicKey;
          setPublicKey(key);
        } catch (e) {
          const s = e?.response?.status;
          if (s === 503) throw new Error('Server push is not configured (VAPID keys missing). Contact the admin.');
          throw new Error(`Could not fetch push key (${s || 'network'}).`);
        }
      }

      // Step 3 — Service worker ready (force an update check first so a stale SW
      // without push handlers is replaced when the user enables notifications).
      let reg;
      try {
        reg = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, rj) => setTimeout(() => rj(new Error('sw-timeout')), 10000)),
        ]);
        try { await reg.update(); } catch (_) { /* ignore */ }
      } catch (e) {
        throw new Error('Service worker never activated. Hard-refresh (Ctrl+Shift+R) and try again.');
      }

      // Step 4 — Subscribe (re-subscribe if stale key)
      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        const existingKey = sub.options?.applicationServerKey;
        const fresh = urlBase64ToUint8Array(key);
        const sameKey = existingKey && new Uint8Array(existingKey).length === fresh.length &&
          new Uint8Array(existingKey).every((b, i) => b === fresh[i]);
        if (!sameKey) {
          try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
          sub = null;
        }
      }
      if (!sub) {
        try {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key),
          });
        } catch (e) {
          const name = e?.name || 'PushError';
          if (name === 'NotAllowedError') throw new Error('Browser refused to create a push subscription. Check notifications are allowed for this site.');
          if (name === 'AbortError') throw new Error('Push service unavailable right now. Check your internet/firewall and try again.');
          if (name === 'InvalidAccessError') throw new Error('Invalid push key configured on the server. Contact the admin.');
          throw new Error(`Could not create push subscription (${name}).`);
        }
      }

      // Step 5 — Persist on backend
      const subJson = sub.toJSON();
      if (!subJson?.keys?.p256dh || !subJson?.keys?.auth) {
        throw new Error('Browser returned an incomplete subscription. Remove the site permission and try again.');
      }
      try {
        await axios.post(
          `${API}/api/notifications/subscribe`,
          {
            endpoint: sub.endpoint,
            keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
            user_agent: navigator.userAgent,
          },
          { withCredentials: true }
        );
      } catch (e) {
        const s = e?.response?.status;
        if (s === 401) throw new Error('You are signed out. Refresh the page and sign in, then retry.');
        throw new Error(`Server rejected the subscription (${s || 'network'}).`);
      }

      // Cache the public key for pushsubscriptionchange recovery in the SW.
      try {
        const cache = await caches.open('cs-vapid');
        await cache.put('/__vapid_public_key', new Response(key));
      } catch (_) { /* ignore */ }

      setSubscribed(true);
      return true;
    } finally {
      setLoading(false);
    }
  }, [supported, publicKey]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await axios.post(
            `${API}/api/notifications/unsubscribe`,
            { endpoint: sub.endpoint },
            { withCredentials: true }
          );
        } catch (_) { /* ignore */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    const res = await axios.post(
      `${API}/api/notifications/test`,
      {},
      { withCredentials: true }
    );
    return res.data;
  }, []);

  const broadcast = useCallback(async (msg) => {
    const res = await axios.post(
      `${API}/api/notifications/broadcast`,
      msg,
      { withCredentials: true }
    );
    return res.data;
  }, []);

  return {
    supported,
    permission,
    subscribed,
    loading,
    publicKey,
    status,
    enable,
    disable,
    sendTest,
    broadcast,
  };
}
